import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNotify, useRefresh } from 'react-admin';
import { PDFFormEditor, usePDFEditor } from '../components/PDFFormEditor';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { useConfirmedFormReset } from './useConfirmedFormReset';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Encode a Uint8Array as base64 in chunks so large PDFs don't blow the
// String.fromCharCode arg cap (same helper the single-form modal uses).
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
};

export type CppFormKey = 'cpp_death_benefit' | 'cpp_survivor_pension';
export type CppFormKeyForm = 'death_benefit' | 'survivor_pension';

const formKeyToSend = (key: CppFormKey): CppFormKeyForm =>
  key === 'cpp_survivor_pension' ? 'survivor_pension' : 'death_benefit';

export interface CppReviewForm {
  key: CppFormKey;
  title: string;
  /** Backend route returning the pre-filled, form-intact PDF. */
  pdfPath: string;
  downloadName: string;
  /** confirmed_at already set (reviewed at least once). */
  confirmed: boolean;
  /** An active signing submission exists. */
  sent: boolean;
  /** A signed copy exists — the form is locked until discarded. */
  signed: boolean;
}

interface CppBenefitsReviewModalProps {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  // The applicable CPP forms, in tab order. One entry (Death Benefit only, no
  // surviving spouse) or two (Death Benefit + Survivor's Pension).
  forms: CppReviewForm[];
  // Which form's tab is active when the modal opens.
  initialFormKey?: CppFormKey;
  discarding?: boolean;
  /** Refresh the sidebar status after a successful prepare. */
  onReadyChange?: () => void;
  // Send flattened + confirmed every applicable (non-signed) form, then hand
  // off to the Send dialog with these marked ready. The Send dialog is where
  // recipients (spouse routing, executor chain) and dispatch are chosen.
  onPrepared: (preparedKeys: CppFormKeyForm[]) => void;
  /** Soft-archive a signed copy so the form can be corrected and resent. */
  onDiscardSigned?: (formKey: CppFormKey) => void;
}

// CPP review modal for the funeral sidebar. Unlike the generic single-form
// BenefitFormReviewModal, this opens BOTH CPP forms at once — one tab each,
// both PDFs loaded simultaneously — so a single Send flattens and confirms
// them together. That restores the "send both to the same person" workflow
// the old two-step Ready→Send lost: there's no way to independently mark one
// form ready anymore, so we prepare every applicable form in one action.
const CppBenefitsReviewModal: React.FC<CppBenefitsReviewModalProps> = ({
  open,
  onClose,
  funeralId,
  forms,
  initialFormKey,
  discarding = false,
  onReadyChange,
  onPrepared,
  onDiscardSigned,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  // Two independent editor controllers — one per possible form. Hooks must run
  // unconditionally, so we always create two even when only one form applies;
  // the unused one simply never receives a PDF.
  const editorA = usePDFEditor();
  const editorB = usePDFEditor();
  const editors = useMemo(() => [editorA, editorB], [editorA, editorB]);

  const [activeTab, setActiveTab] = useState(0);
  const [sending, setSending] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState<CppFormKey | null>(null);
  // Which forms' PDFs have finished loading (keyed by form key) — Send waits
  // until every applicable (non-signed) form is ready to flatten.
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});

  // `forms` is rebuilt as a fresh array on every parent render, so we read it
  // through a ref to keep it out of the reset effect's deps — otherwise every
  // incidental re-render (react-admin refresh, contact fetches) would snap the
  // active tab back and wipe the loaded flags mid-review.
  const formsRef = useRef(forms);
  formsRef.current = forms;

  // Reset transient state and jump to the requested tab only when the modal
  // (re)opens or the requested tab changes. The editors themselves remount
  // because the Dialog unmounts its content on close, so their loaded state
  // starts fresh here too.
  useEffect(() => {
    if (!open) return;
    setLoaded({});
    setSending(false);
    setConfirmDiscard(null);
    const idx = initialFormKey
      ? formsRef.current.findIndex((f) => f.key === initialFormKey)
      : 0;
    setActiveTab(idx >= 0 ? idx : 0);
  }, [open, initialFormKey]);

  const editorFor = (index: number) => editors[index];

  // Idempotent so re-notifying an already-loaded form doesn't churn state.
  const markLoaded = useCallback((key: string) => {
    setLoaded((l) => (l[key] ? l : { ...l, [key]: true }));
  }, []);

  // Stable per-slot onPdfReady handlers. PDFFormEditor's ready effect depends
  // on the callback's identity, so passing a fresh arrow each render would make
  // it re-fire endlessly ("Maximum update depth exceeded"). The hook's
  // handlePdfReady is itself stable; forms are read through the ref.
  const editorAReady = editorA.handlePdfReady;
  const editorBReady = editorB.handlePdfReady;
  const readyHandlers = useMemo(
    () => [
      (bytes: Uint8Array) => {
        editorAReady(bytes);
        const k = formsRef.current[0]?.key;
        if (k) markLoaded(k);
      },
      (bytes: Uint8Array) => {
        editorBReady(bytes);
        const k = formsRef.current[1]?.key;
        if (k) markLoaded(k);
      },
    ],
    [editorAReady, editorBReady, markLoaded],
  );

  // Forms we can prepare this Send: everything applicable that isn't already
  // signed (signed forms are locked — the agent must discard to re-send).
  const preparable = forms.filter((f) => !f.signed);
  const allPreparableLoaded =
    preparable.length === 0 || preparable.every((f) => loaded[f.key]);

  const prepareForm = async (index: number): Promise<boolean> => {
    const form = forms[index];
    const editor = editorFor(index);
    const overrides = editor.getFieldValues();
    if (!overrides) {
      notify(`${form.title} isn't loaded yet — wait for the preview`, {
        type: 'warning',
      });
      return false;
    }
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`,
      'Refresh-Token': await getRefreshToken(),
    };
    const confirmRes = await fetch(`${PAPERMAN_API_URL}form-confirmations/${funeralId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ formKey: form.key, overrides }),
    });
    if (!confirmRes.ok) {
      const errBody = await confirmRes.json().catch(() => ({}));
      throw new Error(errBody?.message || `Confirm failed (${confirmRes.status})`);
    }
    const pdfBase64 = bytesToBase64(await editor.getFlattenedPdfBytes());
    const saveRes = await fetch(`${PAPERMAN_API_URL}cpp-benefits/save-flattened/${funeralId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ formKey: form.key, pdfBase64 }),
    });
    if (!saveRes.ok) {
      const errBody = await saveRes.json().catch(() => ({}));
      throw new Error(errBody?.message || `Save PDF failed (${saveRes.status})`);
    }
    return true;
  };

  // Flatten + confirm every applicable form, then open the Send dialog. Both
  // forms get prepared so the dialog lets the agent send either or both; the
  // dialog is where they untick anything they don't actually want to send.
  const handleSend = async () => {
    setSending(true);
    try {
      const preparedKeys: CppFormKeyForm[] = [];
      for (let i = 0; i < forms.length; i++) {
        if (forms[i].signed) continue;
        const ok = await prepareForm(i);
        if (!ok) return;
        preparedKeys.push(formKeyToSend(forms[i].key));
      }
      onReadyChange?.();
      refresh();
      onPrepared(preparedKeys);
    } catch (err: any) {
      console.error('CPP review prepare error:', err);
      notify(err?.message || 'Failed to prepare forms for sending', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async (index: number) => {
    const form = forms[index];
    try {
      await editorFor(index).downloadFlattenedPdf(form.downloadName);
      notify(`${form.title} downloaded`, { type: 'success' });
    } catch (err) {
      console.error('CPP review download error:', err);
      notify('Failed to download form', { type: 'error' });
    }
  };

  // Reset (unlock) the active tab's confirmed form. Bumping its nonce remounts
  // that editor so it re-fetches the form, now regenerated from the case.
  const [pdfNonce, setPdfNonce] = useState<Record<string, number>>({});
  const current = forms[activeTab];
  const { resetButton, driftBanner, resetDialog } = useConfirmedFormReset({
    funeralId,
    formKey: current?.key,
    title: current?.title,
    confirmed: !!current?.confirmed,
    enabled: open && !!current && !current.signed,
    onReset: () => {
      if (!current) return;
      const key = current.key;
      setLoaded((l) => ({ ...l, [key]: false }));
      setPdfNonce((n) => ({ ...n, [key]: (n[key] || 0) + 1 }));
      onReadyChange?.();
    },
  });

  if (!open || forms.length === 0) return null;

  const anyPreparableSent = preparable.some((f) => f.sent);
  const active = forms[activeTab];
  const sendLabel =
    preparable.length > 1
      ? anyPreparableSent
        ? 'Continue to send'
        : 'Send…'
      : anyPreparableSent
        ? 'Resend'
        : 'Send';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pb: forms.length > 1 ? 0 : undefined }}>
        CPP Benefits
      </DialogTitle>
      {forms.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {forms.map((f) => (
            <Tab
              key={f.key}
              label={
                f.title +
                (f.signed ? ' ✓' : f.sent ? ' • sent' : '')
              }
            />
          ))}
        </Tabs>
      )}
      <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            p: 1,
          }}
        >
          {resetButton}
          <Button
            onClick={() => handleDownload(activeTab)}
            variant="outlined"
            color="primary"
            size="small"
            disabled={!loaded[active.key]}
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
          {active.signed && (
            <Button
              onClick={() => setConfirmDiscard(active.key)}
              variant="outlined"
              color="error"
              size="small"
              disabled={discarding}
              startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            >
              Discard signed copy
            </Button>
          )}
          {preparable.length > 0 && (
            <Button
              onClick={handleSend}
              variant="contained"
              color="secondary"
              size="small"
              disabled={sending || !allPreparableLoaded}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            >
              {sendLabel}
            </Button>
          )}
        </Box>

        {active.signed && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            This form has been signed and is locked. To make changes, discard the
            signed copy first — then you can edit and resend it.
          </Alert>
        )}
        {active.sent && !active.signed && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            This form has already been sent. Editing and sending again will
            invalidate the previous signing link.
          </Alert>
        )}
        {driftBanner}
        {forms.length > 1 && !anyPreparableSent && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            Review both tabs, then <strong>Send…</strong> — both forms are
            prepared together and you choose recipients on the next screen.
          </Alert>
        )}

        <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          {forms.map((f, i) => (
            <Box
              key={f.key}
              sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'auto',
                display: i === activeTab ? 'block' : 'none',
                pointerEvents: f.signed ? 'none' : 'auto',
                opacity: f.signed ? 0.6 : 1,
              }}
            >
              <PDFFormEditor
                key={`${f.key}-${pdfNonce[f.key] || 0}`}
                pdfUrl={`${PAPERMAN_API_URL}${f.pdfPath}/${funeralId}`}
                onPdfReady={readyHandlers[i]}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {resetDialog}

      <Dialog open={!!confirmDiscard} onClose={() => setConfirmDiscard(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard signed copy?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The signed{' '}
            <strong>{forms.find((f) => f.key === confirmDiscard)?.title}</strong>{' '}
            will be archived and removed from the funeral. The form returns to a
            sendable state so you can edit and resend it. The archived copy is
            kept in storage and can be recovered if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(null)} disabled={discarding}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const key = confirmDiscard;
              setConfirmDiscard(null);
              if (key) onDiscardSigned?.(key);
            }}
            variant="contained"
            color="error"
            disabled={discarding}
            startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CppBenefitsReviewModal;
